import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  acknowledgeNotice,
  copyAgent,
  fetchAgentAbout,
  fetchGallery,
  fetchNotifications,
  type GalleryQuery,
  markAllNotificationsRead,
  markNotificationRead
} from '@/api/marketplace.ts'

/**
 * Query hooks for the consumer marketplace.
 *
 * Same discipline as `use-agents.ts`: writes invalidate rather than patch, because every
 * response here is computed per viewer (coverage, `noticesDue`, `copyable`) and a hand-built
 * cache entry would be a second, worse implementation of the server's authorization.
 */

export const marketplaceKeys = {
  all: ['marketplace'] as const,
  gallery: (search: string, category: string) => ['marketplace', 'gallery', search, category] as const,
  about: (agentId: string) => ['marketplace', 'about', agentId] as const,
  notifications: ['notifications'] as const
}

export const useGallery = (query: GalleryQuery = {}, enabled = true) =>
  useQuery({
    queryKey: marketplaceKeys.gallery(query.search ?? '', query.category ?? ''),
    queryFn: () => fetchGallery(query),
    enabled,
    // The search box re-queries per debounced keystroke; keeping recent results fresh for a
    // few seconds makes backspacing through a query instant instead of a second round trip.
    staleTime: 10_000,
    placeholderData: (previous) => previous
  })

/**
 * One listing, as this viewer sees it.
 *
 * Not retried: a 404 here is the *designed* answer for an agent that is not shared with the
 * caller (AGM-002 — denial is indistinguishable from non-existence), and retrying it three
 * times only delays the "not available" screen.
 */
export const useAgentAbout = (agentId: string | undefined) =>
  useQuery({
    queryKey: marketplaceKeys.about(agentId ?? ''),
    queryFn: () => fetchAgentAbout(agentId ?? ''),
    enabled: Boolean(agentId),
    retry: false,
    staleTime: 30_000
  })

export const useAcknowledgeNotice = () => {
  const client = useQueryClient()
  return useMutation({
    mutationFn: ({ agentId, capability }: { agentId: string; capability: 'actions' | 'memory' }) =>
      acknowledgeNotice(agentId, capability),
    onSuccess: (_result, input) => {
      // `noticesDue` is part of the About payload, so the acknowledgement is only visible
      // once that response is refetched.
      void client.invalidateQueries({ queryKey: marketplaceKeys.about(input.agentId) })
    }
  })
}

export const useCopyAgent = () => {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (agentId: string) => copyAgent(agentId),
    onSuccess: () => client.invalidateQueries({ queryKey: marketplaceKeys.all })
  })
}

/**
 * The shell's notification inbox.
 *
 * Polled rather than pushed: the events that land here (a review decided, an agent
 * suspended, health degraded) arrive while the user is somewhere else entirely, and a
 * minute of latency on an inbox badge is invisible where a dedicated socket would not be
 * worth its reconnect logic.
 */
export const useNotifications = (enabled: boolean) =>
  useQuery({
    queryKey: marketplaceKeys.notifications,
    queryFn: () => fetchNotifications(),
    enabled,
    refetchInterval: 60_000,
    staleTime: 30_000
  })

export const useMarkNotificationRead = () => {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => markNotificationRead(id),
    onSuccess: () => client.invalidateQueries({ queryKey: marketplaceKeys.notifications })
  })
}

export const useMarkAllNotificationsRead = () => {
  const client = useQueryClient()
  return useMutation({
    mutationFn: () => markAllNotificationsRead(),
    onSuccess: () => client.invalidateQueries({ queryKey: marketplaceKeys.notifications })
  })
}
